import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ROBYN_PHOTO, API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  User,
  Award,
  Briefcase,
  Heart,
  Shield,
  Users,
  ChevronRight,
  Quote,
  MapPin
} from 'lucide-react';

const AboutPage = () => {
  const [siteContent, setSiteContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API}/site-content`);
        if (response.data && Object.keys(response.data).length > 0) {
          setSiteContent(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch site content:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  // Get about content with fallbacks
  const aboutContent = siteContent?.about || {};
  
  const candidateName = aboutContent.candidate_name || 'Robyn Lyons';
  const candidateTitle = aboutContent.candidate_title || 'PLP Candidate for Killarney';
  const candidateImage = aboutContent.candidate_image || ROBYN_PHOTO;
  const bio = aboutContent.bio || 'Robyn Lyons is a dedicated public servant with a proven track record of community advocacy. As a former Senator and PLP party officer, she has spent years fighting for the people of The Bahamas.';
  const visionTitle = aboutContent.vision_title || 'Our Vision';
  const visionText = aboutContent.vision_text || 'A Killarney where every resident has access to quality services, safe neighborhoods, and opportunities for growth.';

  const timeline = aboutContent.timeline?.length > 0 ? aboutContent.timeline : [
    { year: '2010', event: 'Began community advocacy in Killarney' },
    { year: '2015', event: 'Appointed to the Bahamas Senate' },
    { year: '2018', event: 'Served as PLP Party Officer' },
    { year: '2020', event: 'Led community COVID-19 relief efforts' },
    { year: '2025', event: 'PLP Candidate for Killarney Constituency' }
  ];

  const defaultPriorities = [
    {
      icon: Shield,
      title: 'Community Safety',
      description: 'Implementing "Safer Together" initiative with community watch programs and direct police coordination.'
    },
    {
      icon: Briefcase,
      title: 'Infrastructure Improvement',
      description: 'Fixing roads, streetlights, and public facilities through transparent "Fix It Forward" tracking.'
    },
    {
      icon: Users,
      title: 'Youth Empowerment',
      description: 'Creating opportunities for young Bahamians through education and job training programs.'
    },
    {
      icon: Heart,
      title: 'Senior Care',
      description: 'Ensuring our elders have access to healthcare, transportation, and community support.'
    }
  ];

  // Map dynamic priorities to include icons
  const iconMap = {
    'Community Safety': Shield,
    'Infrastructure Improvement': Briefcase,
    'Infrastructure': Briefcase,
    'Youth Empowerment': Users,
    'Senior Care': Heart
  };

  const priorities = aboutContent.priorities?.length > 0 
    ? aboutContent.priorities.map(p => ({
        ...p,
        icon: iconMap[p.title] || Shield
      }))
    : defaultPriorities;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <section className="relative bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <div className="relative">
              <div className="relative z-10 rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src={candidateImage.startsWith('/api') ? `${API.replace('/api', '')}${candidateImage}` : candidateImage}
                  alt={candidateName}
                  className="w-full h-[500px] object-cover object-top"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-plp-blue to-transparent p-6">
                  <Badge className="bg-plp-gold text-plp-blue mb-2">PLP Candidate</Badge>
                  <h2 className="text-2xl font-heading font-bold text-white">{candidateName}</h2>
                  <p className="text-white/80">{candidateTitle}</p>
                </div>
              </div>
              <div className="absolute -bottom-6 -right-6 w-40 h-40 bg-plp-gold/30 rounded-full blur-3xl"></div>
            </div>

            {/* Content */}
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 bg-plp-blue/10 text-plp-blue px-4 py-2 rounded-full">
                <User className="w-4 h-4" />
                <span className="text-sm font-medium">Meet Your Candidate</span>
              </div>

              <h1 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 tracking-tight">
                A Leader Who <span className="text-plp-blue">Listens</span> and <span className="text-plp-gold">Acts</span>
              </h1>

              <p className="text-lg text-slate-600 leading-relaxed">
                {bio}
              </p>

              <div className="flex flex-wrap gap-3">
                <Badge variant="outline" className="px-4 py-2">
                  <Award className="w-4 h-4 mr-2" />
                  Former Senator
                </Badge>
                <Badge variant="outline" className="px-4 py-2">
                  <Briefcase className="w-4 h-4 mr-2" />
                  PLP Party Officer
                </Badge>
                <Badge variant="outline" className="px-4 py-2">
                  <MapPin className="w-4 h-4 mr-2" />
                  Killarney Resident
                </Badge>
              </div>

              <div className="flex flex-wrap gap-4 pt-4">
                <Link to="/get-involved">
                  <Button className="bg-plp-blue hover:bg-plp-blue-light text-white rounded-full px-8 py-6" data-testid="about-volunteer-btn">
                    <Users className="w-5 h-5 mr-2" />
                    Join the Campaign
                  </Button>
                </Link>
                <Link to="/engage">
                  <Button variant="outline" className="border-2 border-plp-blue text-plp-blue rounded-full px-8 py-6" data-testid="about-engage-btn">
                    Watch Robyn Responds
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-16 bg-plp-blue">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Quote className="w-12 h-12 text-plp-gold mx-auto mb-6" />
          <blockquote className="text-2xl md:text-3xl font-heading text-white italic leading-relaxed">
            "{visionText}"
          </blockquote>
          <p className="mt-6 text-plp-gold font-heading font-semibold">— {candidateName}</p>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl font-bold text-slate-900 text-center mb-12">
            A Journey of Service
          </h2>
          
          <div className="relative">
            {/* Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-slate-200 hidden md:block"></div>
            
            <div className="space-y-8">
              {timeline.map((item) => (
                <div key={item.year} className={`flex items-center gap-8 ${timeline.indexOf(item) % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                  <div className={`flex-1 ${timeline.indexOf(item) % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                    <Card className="inline-block bg-slate-50" data-testid={`timeline-${item.year}`}>
                      <CardContent className="p-6">
                        <Badge className="bg-plp-blue text-white mb-2">{item.year}</Badge>
                        <p className="text-slate-700">{item.event}</p>
                      </CardContent>
                    </Card>
                  </div>
                  <div className="w-4 h-4 bg-plp-gold rounded-full border-4 border-white shadow-lg z-10 hidden md:block"></div>
                  <div className="flex-1 hidden md:block"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Priorities */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-slate-900">
              Priorities for Killarney
            </h2>
            <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">
              Robyn's platform focuses on what matters most to Killarney residents.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {priorities.map((priority) => (
              <Card key={priority.title} className="bg-white hover:shadow-lg transition-all" data-testid={`priority-${priority.title.toLowerCase().replace(/\s+/g, '-')}`}>
                <CardContent className="p-6 flex gap-4">
                  <div className="w-14 h-14 bg-plp-blue/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <priority.icon className="w-7 h-7 text-plp-blue" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-xl text-slate-900">{priority.title}</h3>
                    <p className="mt-2 text-slate-600">{priority.description}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-plp-blue to-plp-blue-light">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Build a Better Killarney?
          </h2>
          <p className="text-white/80 text-lg mb-8">
            Join Robyn Lyons and the PLP in creating positive change for our community.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/get-involved">
              <Button className="bg-plp-gold hover:bg-plp-gold-light text-plp-blue font-bold rounded-full px-8 py-6 text-lg" data-testid="about-cta-volunteer">
                Volunteer Now
              </Button>
            </Link>
            <Link to="/safety-map">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg" data-testid="about-cta-safety">
                Report an Issue
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
